import graphql__319ae49745fcce31efad from "./__generated__/AnalysisForecastMinimumBalanceMutation.graphql.ts";import * as React from "react";
import {useState} from "react";
import {graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import {faPencil} from "@fortawesome/pro-regular-svg-icons";
import ActionButton from "./presentational/button/ActionButton";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";
import {formatCentsForInput, parseAmountToCents} from "../helper/amountInput";
import {useErrorContext} from "../contexts/ErrorContext";
import type {AnalysisForecastMinimumBalanceMutation} from "./__generated__/AnalysisForecastMinimumBalanceMutation.graphql";

type Props = {
    minimumBalance: number | null // cents, null if none is set
    formatAmount: (cents: number) => string
    onSaved: () => void
};

const minimumBalanceMutation = graphql__319ae49745fcce31efad;

/**
 * The minimum balance of the bank accounts, which the forecast warns about falling below, with an
 * inline form to change it.
 */
const AnalysisForecastMinimumBalance: React.FC<Props> = ({minimumBalance, formatAmount, onSaved}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const [editedMinimum, setEditedMinimum] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const saveMinimumBalance = () => {
        const cents = parseAmountToCents(editedMinimum ?? '');
        if (cents === null || cents < 0) {
            return;
        }
        setIsSaving(true);
        commitMutationWithErrorHandling<AnalysisForecastMinimumBalanceMutation>(
            relayEnvironment,
            {
                mutation: minimumBalanceMutation,
                variables: {input: {amount: cents}},
                onCompleted: () => {
                    setIsSaving(false);
                    setEditedMinimum(null);
                    onSaved();
                },
                onError: () => setIsSaving(false),
            },
            addError,
            {customErrorMessage: intl.formatMessage({id: 'errors.failedToSetMinimumBalance'})}
        );
    };
    const isMinimumValid = editedMinimum !== null && (parseAmountToCents(editedMinimum) ?? -1) >= 0;

    return (
        <div>
            <div className="text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisForecastOverview.minimumBalance'})}</div>
            {editedMinimum === null ? (
                <div className="flex items-center gap-2">
                    <span className="text-2xl font-semibold tabular-nums text-gray-900">
                        {minimumBalance === null ? intl.formatMessage({id: 'view.analysisForecastOverview.minimumBalance.notSet'}) : formatAmount(minimumBalance)}
                    </span>
                    <ActionButton
                        icon={faPencil}
                        title={intl.formatMessage({id: 'view.analysisForecastOverview.minimumBalance.edit'})}
                        onClick={() => setEditedMinimum(formatCentsForInput(minimumBalance ?? 0))}
                        additionalClassname=""
                    />
                </div>
            ) : (
                <form className="flex items-center gap-2" onSubmit={event => { event.preventDefault(); saveMinimumBalance(); }}>
                    <input
                        type="text"
                        inputMode="decimal"
                        autoFocus
                        value={editedMinimum}
                        onChange={event => setEditedMinimum(event.target.value)}
                        onKeyUp={event => { if (event.key === 'Escape') { setEditedMinimum(null); } }}
                        className={`w-40 border px-2 py-1 text-right text-lg tabular-nums ${isMinimumValid ? 'border-gray-300' : 'border-red-400'}`}
                        aria-label={intl.formatMessage({id: 'view.analysisForecastOverview.minimumBalance'})}
                    />
                    <span className="text-gray-500">€</span>
                    <ActionButton
                        type="submit"
                        label={intl.formatMessage({id: 'view.analysisForecastOverview.minimumBalance.save'})}
                        disabled={!isMinimumValid}
                        isSubmitting={isSaving}
                        additionalClassname=""
                    />
                    <ActionButton
                        type="button"
                        label={intl.formatMessage({id: 'view.analysisForecastOverview.minimumBalance.cancel'})}
                        onClick={() => setEditedMinimum(null)}
                        additionalClassname=""
                    />
                </form>
            )}
            <div className="text-xs text-gray-500">{intl.formatMessage({id: 'view.analysisForecastOverview.minimumBalance.description'})}</div>
        </div>
    );
};

export default AnalysisForecastMinimumBalance;
